import { IBookedTrips } from "../../../Orchestrator/Types/Index";
import { IPassenger } from "../../../Orchestrator/Types/Interface/IPassenger";

/**
 * ACPT-037 — one booking card per JOURNEY of a booking reference on /bookings:
 * one per booking, unless a partial change-date-time split it.
 *
 * The list mapper (GetBookingToRightDataAction) grows a row per ORDER LINE, not
 * per booking: a row per trip on the split-round-trip path, a row per
 * passenger group on the change-date-time path, and a row per passenger as soon
 * as a Nol pass is in the basket — a pass is sold on its own booked offer,
 * covering one passenger, with no `tripCoverage.coveredTripId`, which reads to
 * the CDT splitter exactly like a partial-passenger exchange.
 *
 * Rather than teach every branch of that 3000-line mapper the same lesson, the
 * rows it produces are folded here, at the one place they reach the atom: one
 * card per journey, summarising its passengers, legs and fulfilments. A
 * change-date that moved SOME passengers is the one thing that makes a second
 * journey — the moved group keeps its own card, as mobile's splitCommonBooking
 * does ("one card per passenger group the exchange created — the passes add
 * none"). See isSameJourney.
 *
 * Collapsing happens AFTER the Upcoming/Past filter, deliberately. A round trip
 * whose outbound has flown and whose inbound has not belongs in both tabs, and
 * folding before the filter would pick one departure date for the whole booking
 * and drop the booking out of the other tab.
 */

/**
 * Which row speaks for the booking when several describe it. Lower is better.
 * A live leg outranks a cancelled one so the card does not read "Refunded"
 * while half the booking is still travelling.
 */
const STATUS_RANK: Record<string, number> = {
    FULFILLED: 0,
    EXCHANGE_ONGOING: 1,
    ON_HOLD: 2,
    EXCHANGED: 3,
    REFUNDED: 4,
};

const statusRank = (status?: string): number =>
    status && status in STATUS_RANK ? STATUS_RANK[status] : STATUS_RANK.EXCHANGED;

const departureMs = (row: IBookedTrips): number => {
    const time = new Date(row?.departureDateTime || "").getTime();
    return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};

/** A shuttle row must not name the card while a train row is available. */
const isShuttleRow = (row: any): boolean =>
    row?.isOutboundIsDirectTrain === false ||
    !!row?.summary?.toLowerCase?.().includes("shuttle");

const uniqueStrings = (values: (string | undefined | null)[]): string[] =>
    Array.from(new Set(values.filter((value): value is string => !!value)));

/** Union of passengers across rows, first appearance wins, keyed by id. */
const mergePassengers = (rows: IBookedTrips[]): IPassenger[] => {
    const byId = new Map<string, IPassenger>();
    rows.forEach((row) => {
        (row?.passengers || []).forEach((passenger: IPassenger) => {
            const id = passenger?.id;
            if (!id || byId.has(id)) return;
            byId.set(id, passenger);
        });
    });
    return Array.from(byId.values());
};

/**
 * The inbound half of the card, when the mapper emitted the return leg as its
 * own one-way row instead of attaching it to the outbound row. Mirrors the
 * shape `createBookingForPassengers` builds so the card renders it unchanged.
 */
const inboundDisplayFromRow = (row: any) => ({
    fromName: row?.fromName,
    toName: row?.toName,
    from: row?.from,
    to: row?.to,
    duration: row?.duration,
    stops: row?.stops || [],
    departureTime: row?.departureTime,
    arrivalTime: row?.arrivalTime,
    arrivalDateTime: row?.arrivalDateTime,
    departureDateTime: row?.departureDateTime,
    status: row?.status,
    offerId: row?.outBoundOfferID,
    summary: row?.summary,
    isRetrieveBooking: row?.isRetrieveBooking || false,
    inboundConfirmedPrice: row?.inboundConfirmedPrice,
    selectedPriceClass: row?.selectedPriceClass,
    productName: row?.productName,
    fulfillmentIds: row?.fulfillmentIds || [],
});

const pickPrimary = (rows: IBookedTrips[]): IBookedTrips =>
    rows.reduce((best, candidate) => {
        const byStatus = statusRank(candidate.status) - statusRank(best.status);
        if (byStatus !== 0) return byStatus < 0 ? candidate : best;

        const byTrain = Number(isShuttleRow(candidate)) - Number(isShuttleRow(best));
        if (byTrain !== 0) return byTrain < 0 ? candidate : best;

        // Earliest departure: on a round trip that is the outbound leg, which
        // is what the card leads with.
        return departureMs(candidate) < departureMs(best) ? candidate : best;
    });

const mergeGroup = (rows: IBookedTrips[]): IBookedTrips => {
    const primary: any = pickPrimary(rows);
    const others: any[] = rows.filter((row) => row !== primary);

    // The return leg: either already attached to a row, or standing alone as an
    // inbound one-way row that this fold has to reattach.
    const rowWithInbound: any = rows.find((row: any) => !!row?.inboundData);
    const standaloneInbound: any = others.find(
        (row: any) => !row?.inboundData && row?.direction === "IN_BOUND",
    );
    const inboundData = rowWithInbound
        ? rowWithInbound.inboundData
        : standaloneInbound
            ? inboundDisplayFromRow(standaloneInbound)
            : primary.inboundData;
    const inboundOfferID = rowWithInbound
        ? rowWithInbound.inboundOfferID
        : standaloneInbound
            ? standaloneInbound.outBoundOfferID
            : primary.inboundOfferID;

    const all: any[] = rows;
    return {
        ...primary,
        status: all.reduce(
            (best: string, row: any) => (statusRank(row?.status) < statusRank(best) ? row.status : best),
            primary.status,
        ),
        passengers: mergePassengers(rows),
        bookingType: inboundData ? "roundTrip" : primary.bookingType,
        inboundData,
        inboundOfferID,
        // Primary first: readers take tripIds[0] as the leg the card leads with.
        tripIds: uniqueStrings([primary, ...others].flatMap((row: any) => row?.tripIds || [])),
        fulfillmentIds: uniqueStrings(all.flatMap((row: any) => row?.fulfillmentIds || [])),
        passengerData: {
            outbound: uniqueStrings(all.flatMap((row: any) => row?.passengerData?.outbound || [])),
            inbound: uniqueStrings(all.flatMap((row: any) => row?.passengerData?.inbound || [])),
        },
        isBookingHasShuttle: all.some((row: any) => !!row?.isBookingHasShuttle),
        isShuttleOnlyBooking: all.every((row: any) => !!row?.isShuttleOnlyBooking),
        canAddShuttleUntil: latestShuttleUntil(all),
    } as IBookedTrips;
};

/**
 * The latest canAddShuttleUntil across a booking's rows — any row still open
 * keeps the card's item. null if every row was stamped closed, undefined if none
 * was stamped (the card's station fallback).
 */
const latestShuttleUntil = (rows: any[]): number | null | undefined => {
    const stamped = rows.map((row) => row?.canAddShuttleUntil).filter((until) => until !== undefined);
    if (stamped.length === 0) return undefined;
    const instants = stamped.filter((until): until is number => typeof until === "number");
    return instants.length > 0 ? Math.max(...instants) : null;
};

const rowPassengerIds = (row: any): string[] =>
    uniqueStrings((row?.passengers || []).map((passenger: IPassenger) => passenger?.id));

const tripSet = (row: any): Set<string> => new Set(uniqueStrings(row?.tripIds || []));

/** Every trip of `a` is on `b`, and `b` has more. */
const isStrictTripSubset = (a: any, b: any): boolean => {
    const own = tripSet(a);
    const other = tripSet(b);
    return own.size < other.size && Array.from(own).every((id) => other.has(id));
};

/** Options for one fold. */
interface ICollapseOptions {
    /**
     * Join rows on a shared passenger only when they name the SAME passengers.
     * For the next-ticket rebuild, whose rows are per LEG: there a shared rider
     * is the moved passenger, on both the unchanged inbound (whole party) and
     * their new outbound, and folding those drew one round trip nobody holds.
     * The two legs of one group still fold — same riders, same card.
     */
    sameRidersOnly?: boolean;
}

/**
 * The trains a row draws, by departure and stops rather than trip id. An
 * upgrade-to-premium for part of the party can re-issue the SAME train under a
 * new trip id; without this its riders got a second card for a train they
 * share.
 *
 * A change-date matches here only if it landed on the very train it left. Web's
 * exchange search does not leave the current train out (mobile's does, through
 * `excludedTripsStartTimes`), but exchanges are restricted to the same product
 * (#366), so that is a no-op exchange onto the same train in the same class —
 * one card is right for it. A class-only move on one train cannot come from a
 * change-date; if #366 is ever lifted, revisit this.
 */
const physicalKey = (row: any): string =>
    [
        row?.departureDateTime, row?.from, row?.to,
        row?.inboundData?.departureDateTime, row?.inboundData?.from, row?.inboundData?.to,
    ].join("|");

const hasSameMembers = (a: Set<string>, b: Set<string>): boolean =>
    a.size === b.size && Array.from(a).every((id) => b.has(id));

/**
 * Two rows of one booking describe the same journey when they name the same
 * trips (a row per Nol pass, per refunded rider), the same trains (see
 * physicalKey) or share a passenger (the legs of a split round trip). A
 * change-date-time that moved SOME passengers does none of these: each
 * passenger group's row names its own train, and each is its own card — the
 * contract mobile ships (splitCommonBooking, "one card per passenger group the
 * exchange created — the passes add none"). Folding those put both trains and
 * every passenger on one card, and booking details then scoped its trip
 * summary, Nol count and booking summary to the lot (SLCP4M).
 *
 * Sharing a trip alone is NOT enough: after a round trip's outbound change,
 * both groups' cards still carry the unchanged inbound.
 */
const isSameJourney = (a: any, b: any, options: ICollapseOptions): boolean => {
    if (hasSameMembers(tripSet(a), tripSet(b))) return true;
    if (a?.departureDateTime && physicalKey(a) === physicalKey(b)) return true;
    const passengers = new Set(rowPassengerIds(a));
    const otherPassengers = new Set(rowPassengerIds(b));
    if (options.sameRidersOnly) return hasSameMembers(passengers, otherPassengers);
    return Array.from(passengers).some((id) => otherPassengers.has(id));
};

/** Connected components of isSameJourney. */
const componentsOf = (rows: IBookedTrips[], options: ICollapseOptions): IBookedTrips[][] =>
    rows.reduce<IBookedTrips[][]>((journeys, row) => {
        const touching = journeys.filter((journey) => journey.some((other) => isSameJourney(row, other, options)));
        const rest = journeys.filter((journey) => !touching.includes(journey));
        return [...rest, [...touching.flat(), row]];
    }, []);

/** The rows of one card, and — for a partial view — the only riders it may name. */
interface IJourney {
    rows: IBookedTrips[];
    riders?: Set<string>;
}

/**
 * A card narrowed to `riders` — the partial-view case in journeysOf.
 *
 * Deliberately PARTIAL: only who the card names (`passengers`, `passengerData`)
 * is narrowed. `fulfillmentIds`, `tripIds` and `inboundData` still describe the
 * whole partial view; the list reads none of them per rider (BookingOptions
 * recomputes fulfilments per trip), so do not trust them as this card's own.
 */
const narrowedTo = (card: any, riders: Set<string>): IBookedTrips => ({
    ...card,
    passengers: (card?.passengers || []).filter((passenger: IPassenger) => riders.has(passenger?.id)),
    passengerData: {
        outbound: (card?.passengerData?.outbound || []).filter((id: string) => riders.has(id)),
        inbound: (card?.passengerData?.inbound || []).filter((id: string) => riders.has(id)),
    },
});

const isRiderless = (row: IBookedTrips): boolean => rowPassengerIds(row).length === 0;

/** Every trip id the rows of one journey name. */
const tripsOf = (journeyRows: IBookedTrips[]): Set<string> =>
    tripSet({ tripIds: journeyRows.flatMap((row: any) => row?.tripIds || []) });

/**
 * Partial views naming riders no fuller row carries: each folds into the
 * journey with exactly its trains, narrowed to those riders — the ones on no
 * fuller row, so already on no card; the rest stand as their own cards,
 * narrowed the same way.
 */
const placeOrphans = (
    journeys: IBookedTrips[][],
    orphans: IBookedTrips[],
    isUnseen: (id: string) => boolean,
    options: ICollapseOptions
): { joined: IBookedTrips[][]; standalone: IJourney[] } => {
    const orphanJourneys = componentsOf(orphans, options);
    const matches = (orphan: IBookedTrips[], journeyRows: IBookedTrips[]) =>
        hasSameMembers(tripsOf(orphan), tripsOf(journeyRows));
    const joined = journeys.map((journeyRows) => {
        const joining = orphanJourneys.filter((orphan) => matches(orphan, journeyRows)).flat();
        const unseenRiders = new Set(joining.flatMap(rowPassengerIds).filter(isUnseen));
        return [...journeyRows, ...joining.map((row) => narrowedTo(row, unseenRiders))];
    });
    const standalone = orphanJourneys
        .filter((orphan) => !journeys.some((journeyRows) => matches(orphan, journeyRows)))
        .map((journeyRows) => ({ rows: journeyRows, riders: new Set(journeyRows.flatMap(rowPassengerIds).filter(isUnseen)) }));
    return { joined, standalone };
};

/**
 * Riderless rows: each joins the journey whose trips hold all of its own; one
 * spanning several is dropped (every trip it names is drawn on one of them, and
 * joining any would draw the others' trains there too); one matching no trip
 * joins the first journey.
 */
const placeRiderless = (journeys: IBookedTrips[][], riderless: IBookedTrips[]): IBookedTrips[][] => {
    const homeOf = (row: IBookedTrips): number => {
        const trips = Array.from(tripSet(row));
        const home = journeys.findIndex((journeyRows) => trips.every((id) => tripsOf(journeyRows).has(id)));
        if (home >= 0) return home;
        return journeys.some((journeyRows) => trips.some((id) => tripsOf(journeyRows).has(id))) ? -1 : 0;
    };
    return journeys.map((journeyRows, index) => [
        ...journeyRows,
        ...riderless.filter((row) => homeOf(row) === index),
    ]);
};

/**
 * One booking's rows, grouped into the cards they make.
 *
 * Two kinds of row stay out of the split itself, because either could bridge
 * passenger groups:
 *  - a row whose trips are a strict subset of another row's (an inbound-only
 *    row beside the round-trip cards) — a partial view of a fuller card. Its
 *    riders already on a fuller row are shown there; any others (a refunded
 *    leg beside a partial change-date) get a card naming them alone, rather
 *    than vanishing or showing the rest twice. Such partial views fold with
 *    each other, so per-pass rows on one leg still make one card, and one
 *    covering exactly a fuller card's trains joins that card — what the
 *    next-ticket re-fold would do, so the two surfaces agree.
 *    A joining partial view is narrowed to its unseen riders first, so a
 *    rider already on another card is not drawn twice, nor on a train they
 *    left.
 *  - a row naming no passenger, which proves nothing about groups. It joins
 *    the journey whose trips hold all of its own; one spanning several is
 *    dropped (each trip it names is already drawn); one matching no trip joins
 *    the journey holding the booking's first row — never a card of its own,
 *    which would name nobody.
 *
 * When the booking is a single journey anyway, every row is folded together
 * exactly as before.
 */
const journeysOf = (rows: IBookedTrips[], options: ICollapseOptions): IJourney[] => {
    // Subset-of a RIDERLESS row does not count: one naming every trip would
    // otherwise make each passenger row a "partial view" and fold them all.
    const fullest = rows.filter(
        (row) => !isRiderless(row) && !rows.some((other) => !isRiderless(other) && isStrictTripSubset(row, other))
    );
    const firstAppearance = (journeyRows: IBookedTrips[]) => Math.min(...journeyRows.map((row) => rows.indexOf(row)));
    const journeys = componentsOf(fullest, options).sort((a, b) => firstAppearance(a) - firstAppearance(b));
    if (journeys.length <= 1) return [{ rows }];

    const onFullerRows = new Set(fullest.flatMap(rowPassengerIds));
    const isUnseen = (id: string) => !onFullerRows.has(id);
    const orphans = rows.filter((row) => !fullest.includes(row) && rowPassengerIds(row).some(isUnseen));
    const { joined, standalone } = placeOrphans(journeys, orphans, isUnseen, options);
    const homed = placeRiderless(joined, rows.filter(isRiderless));
    return [...homed.map((journeyRows) => ({ rows: journeyRows })), ...standalone];
};

/**
 * Fold list rows down to one card per journey of a booking reference — one per
 * booking, unless a partial change-date-time split it (see isSameJourney).
 *
 * Order is preserved by first appearance, and a journey that already has a
 * single row is returned untouched — same object identity, so the next-ticket
 * comparison downstream keeps matching on it — except a narrowed partial view
 * (narrowedTo), which is a new object by design.
 */
export const collapseBookingRows = (rows: IBookedTrips[], options: ICollapseOptions = {}): IBookedTrips[] => {
    if (!rows || rows.length < 2) return rows || [];

    const groups = new Map<string, IBookedTrips[]>();
    rows.forEach((row) => {
        // No reference, nothing to group on — such a row keeps its own slot
        // rather than being merged with every other reference-less row.
        const key = row?.bookingId;
        if (!key) return;
        const group = groups.get(key);
        if (group) group.push(row);
        else groups.set(key, [row]);
    });

    const journeyOf = new Map<IBookedTrips, IJourney>();
    groups.forEach((group) =>
        journeysOf(group, options).forEach((journey) => journey.rows.forEach((row) => journeyOf.set(row, journey)))
    );

    const emitted = new Set<IJourney>();
    return rows.reduce<IBookedTrips[]>((output, row) => {
        if (!row?.bookingId) return [...output, row];
        // A partial-view row journeysOf left out of a split: its riders are on a fuller card.
        const journey = journeyOf.get(row);
        if (!journey) return output;
        if (emitted.has(journey)) return output;
        emitted.add(journey);
        const card = journey.rows.length === 1 ? journey.rows[0] : mergeGroup(journey.rows);
        return [...output, journey.riders ? narrowedTo(card, journey.riders) : card];
    }, []);
};
